import { useTranslation } from "react-i18next";
import React, { useEffect, useState } from "react";
import { Building2, ShoppingBag, Wallet, Star, Clock } from "lucide-react";
import { apiClient } from "@/lib/api";
import { formatGNF, imgUrl, STATUS_LABEL } from "@/utils/format";
import { Link } from "react-router-dom";
import { BrandLoader } from "@/components/BrandLoader";

function Kpi({ label, value, sub, testId }) {
  return (
    <div className="bg-white rounded-2xl border border-sand p-5" data-testid={testId}>
      <div className="text-xs uppercase tracking-widest text-mute mb-1">{label}</div>
      <div className="font-display text-2xl font-semibold">{value}</div>
      {sub && <div className="text-xs text-mute mt-1">{sub}</div>}
    </div>
  );
}

export default function GroupDashboard() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    apiClient.get(`/my/group/overview?days=${days}`).then(({ data }) => setData(data)).catch(() => setData({ restaurants: [], totals: {}, today: {}, recent_orders: [] }));
  }, [days]);

  if (!data) return <BrandLoader message={t("gdash.loading")} />;

  const { restaurants = [], totals = {}, today = {}, recent_orders = [] } = data;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="group-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-brand-red mb-1 flex items-center gap-1.5">
            <Building2 className="w-3.5 h-3.5" /> {t("gdash.kicker")}
          </div>
          <h1 className="font-display text-3xl font-semibold">{t("gdash.title", { n: restaurants.length })}</h1>
        </div>
        <div className="flex gap-1.5">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => setDays(d)} data-testid={`group-days-${d}`}
              className={`rounded-full px-3.5 py-1.5 text-sm border ${days === d ? "bg-ink text-white border-ink" : "bg-white border-sand"}`}>
              {t("gdash.daysBtn", { d })}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Kpi testId="group-kpi-ca" label={t("gdash.kpiCA", { d: days })} value={formatGNF(totals.subtotal || 0)} sub={t("gdash.kpiToday", { v: formatGNF(today.subtotal || 0) })} />
        <Kpi testId="group-kpi-orders" label={t("gdash.kpiOrders", { d: days })} value={totals.orders || 0} sub={t("gdash.kpiToday", { v: today.orders || 0 })} />
        <Kpi testId="group-kpi-delivered" label={t("gdash.kpiDelivered")} value={totals.delivered || 0} sub={t("gdash.kpiCancelled", { n: totals.cancelled || 0 })} />
        <Kpi testId="group-kpi-gmv" label={t("gdash.kpiVolume", { d: days })} value={formatGNF(totals.revenue || 0)} sub={t("gdash.kpiVolumeSub")} />
      </div>

      <h2 className="font-display text-xl font-semibold mb-3">{t("gdash.byOutlet")}</h2>
      <div className="bg-white rounded-2xl border border-sand overflow-x-auto mb-8">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="text-left text-xs uppercase tracking-widest text-mute border-b border-sand">
              <th className="px-4 py-3">{t("gdash.thOutlet")}</th>
              <th className="px-4 py-3">{t("gdash.thStatus")}</th>
              <th className="px-4 py-3 text-right">{t("gdash.thCA", { d: days })}</th>
              <th className="px-4 py-3 text-right">{t("gdash.thOrders")}</th>
              <th className="px-4 py-3 text-right">{t("gdash.thToday")}</th>
              <th className="px-4 py-3 text-right">{t("gdash.thRating")}</th>
            </tr>
          </thead>
          <tbody>
            {restaurants.map((r) => (
              <tr key={r.restaurant_id} className="border-b border-sand/60 last:border-0" data-testid={`group-outlet-${r.restaurant_id}`}>
                <td className="px-4 py-3">
                  <Link to={`/restaurants/${r.restaurant_id}`} className="flex items-center gap-2.5 hover:text-brand-red">
                    {r.logo_image && <img src={imgUrl(r.logo_image)} alt="" className="w-8 h-8 rounded-lg object-cover" />}
                    <div>
                      <div className="font-semibold">{r.name.replace("Ratoma Fried Chicken (RFC) – ", "RFC ")}</div>
                      <div className="text-xs text-mute">{r.zone}</div>
                    </div>
                  </Link>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${r.open ? "bg-[#E7F6EE] text-brand-green" : "bg-elevated text-mute"}`}>
                    {r.open ? t("gdash.open") : t("gdash.closed")}
                  </span>
                </td>
                <td className="px-4 py-3 text-right font-semibold">{formatGNF(r.period.subtotal || 0)}</td>
                <td className="px-4 py-3 text-right">{r.period.orders || 0} <span className="text-xs text-mute">({r.period.delivered || 0} {t("gdash.delivered")})</span></td>
                <td className="px-4 py-3 text-right">{r.today.orders || 0} {t("gdash.cmd")} · {formatGNF(r.today.subtotal || 0)}</td>
                <td className="px-4 py-3 text-right">
                  <span className="inline-flex items-center gap-1"><Star className="w-3.5 h-3.5 fill-[#F4C430] text-[#F4C430]" /> {(r.rating || 0).toFixed(1)}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2 className="font-display text-xl font-semibold mb-3">{t("gdash.recent")}</h2>
      <div className="space-y-2" data-testid="group-recent-orders">
        {recent_orders.length === 0 && <div className="text-mute text-sm bg-white border border-sand rounded-2xl p-5">{t("gdash.noOrders")}</div>}
        {recent_orders.map((o) => (
          <div key={o.order_id} className="bg-white border border-sand rounded-2xl px-4 py-3 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-3">
              <ShoppingBag className="w-4 h-4 text-brand-red" />
              <div>
                <div className="text-sm font-semibold">{(o.restaurant_name || "").replace("Ratoma Fried Chicken (RFC) – ", "RFC ")} <span className="text-xs text-mute">· {o.zone}</span></div>
                <div className="text-xs text-mute flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(o.created_at).toLocaleString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs uppercase tracking-widest bg-elevated px-2 py-0.5 rounded-full">{t(`status.${o.status}`, STATUS_LABEL[o.status] || o.status)}</span>
              <span className="font-semibold text-sm inline-flex items-center gap-1"><Wallet className="w-3.5 h-3.5 text-brand-green" /> {formatGNF(o.total)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
